import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Zap, RefreshCw, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REFRESH_INTERVAL_MS = 60_000;
const DAILY_BUDGET_CREDITS = 5000;   // yellow-flag threshold per day
const DAILY_ALARM_CREDITS  = 15000;  // red-flag threshold per day

/**
 * ScrapflyUsageTile — admin dashboard card.
 *
 * Shows daily Scrapfly credit spend so runaway daemon loops get spotted
 * before the monthly cap.  Displays a 7-day sparkline, latest call
 * status, and a one-click "retry previously-blocked URLs" button that
 * flips fetch_failed unis back to `status=retry` on the offer-rate
 * daemon.
 */
export default function ScrapflyUsageTile() {
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState(null);
  const [err, setErr] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, h] = await Promise.all([
        api.get("/admin/scrapfly/usage?days=7"),
        api.get("/admin/scrapfly/usage-history?days=7").catch(() => ({ data: null })),
      ]);
      setUsage(u.data);
      setHistory(h.data);
      setLastRefreshedAt(new Date());
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load Scrapfly usage");
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const retry = async (includeEmpty) => {
    if (retrying) return;
    setRetrying(true);
    try {
      const r = await api.post(
        `/admin/scrapfly/retry-blocked?country=UK&include_empty=${includeEmpty}`
      );
      toast.success(r.data.message || `Flipped ${r.data.modified} unis`, {
        duration: 6000,
      });
      setTimeout(load, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry-blocked failed");
    } finally {
      setRetrying(false);
    }
  };

  if (err) {
    return (
      <div
        data-testid="scrapfly-usage-tile-error"
        className="border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900"
      >
        <AlertTriangle className="inline h-4 w-4 mr-1" />
        Scrapfly usage tile failed to load: {err}
      </div>
    );
  }
  if (!usage) {
    return (
      <div
        data-testid="scrapfly-usage-tile-loading"
        className="border border-stone-200 rounded-xl p-4 text-sm text-stone-500"
      >
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" />
        Loading Scrapfly usage…
      </div>
    );
  }

  const series = history?.series || [];
  const todayCredits = series.length ? series[series.length - 1].credits : 0;
  const budgetState =
    todayCredits >= DAILY_ALARM_CREDITS
      ? { color: "text-rose-700", bg: "bg-rose-100", label: "OVER BUDGET" }
      : todayCredits >= DAILY_BUDGET_CREDITS
      ? { color: "text-amber-800", bg: "bg-amber-100", label: "HEADS-UP" }
      : { color: "text-emerald-800", bg: "bg-emerald-100", label: "HEALTHY" };

  const maxCredits = Math.max(1, ...series.map((s) => s.credits));

  return (
    <div
      data-testid="scrapfly-usage-tile"
      className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-indigo-50">
            <Zap className="h-5 w-5 text-indigo-700" />
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Scrapfly Tier-3.5 usage
            </div>
            <div className="text-xs text-stone-500">
              Anti-bot proxy — {usage.window_days}-day window · block-rate{" "}
              {(usage.block_rate * 100).toFixed(0)}%
            </div>
          </div>
        </div>
        <button
          data-testid="scrapfly-refresh-btn"
          onClick={load}
          className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
          title={lastRefreshedAt ? `Last refresh: ${lastRefreshedAt.toLocaleTimeString()}` : ""}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Summary row */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div data-testid="scrapfly-total-calls" className="rounded-lg border border-stone-100 p-3">
          <div className="text-2xl font-semibold text-stone-900">{usage.calls}</div>
          <div className="text-xs text-stone-500 mt-1">calls (7d)</div>
        </div>
        <div data-testid="scrapfly-total-credits" className="rounded-lg border border-stone-100 p-3">
          <div className="text-2xl font-semibold text-stone-900">
            {usage.credits.toLocaleString()}
          </div>
          <div className="text-xs text-stone-500 mt-1">credits burned</div>
        </div>
        <div data-testid="scrapfly-today-credits" className="rounded-lg border border-stone-100 p-3">
          <div className="text-2xl font-semibold text-stone-900">
            {todayCredits.toLocaleString()}
          </div>
          <div className="text-xs text-stone-500 mt-1">today</div>
          <span
            className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${budgetState.bg} ${budgetState.color}`}
          >
            {budgetState.label}
          </span>
        </div>
      </div>

      {/* Sparkline */}
      {series.length > 0 && (
        <div data-testid="scrapfly-sparkline" className="mt-4">
          <div className="text-xs text-stone-500 mb-1">Credits/day (7d)</div>
          <div className="flex items-end gap-1 h-14">
            {series.map((s) => (
              <div
                key={s.date}
                className="flex-1 bg-indigo-500/70 rounded-t"
                style={{
                  height: `${Math.max(2, (s.credits / maxCredits) * 100)}%`,
                }}
                title={`${s.date}: ${s.credits} credits, ${s.calls} calls`}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-stone-400 mt-1">
            <span>{series[0]?.date?.slice(5)}</span>
            <span>{series[series.length - 1]?.date?.slice(5)}</span>
          </div>
        </div>
      )}

      {/* Recent calls */}
      {usage.recent && usage.recent.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-stone-500 mb-2">Recent calls</div>
          <ul data-testid="scrapfly-recent-calls" className="space-y-1">
            {usage.recent.slice(0, 5).map((r, i) => (
              <li
                key={i}
                className="text-xs flex items-center gap-2 text-stone-700"
              >
                <span
                  className={`inline-block w-10 text-center rounded ${
                    r.status < 400 ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                  } text-[10px] font-medium py-0.5`}
                >
                  {r.status}
                </span>
                <span className="text-stone-400">{r.cost_credits || "—"}c</span>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="truncate hover:underline flex-1"
                >
                  {r.url}
                </a>
                <ExternalLink className="h-3 w-3 text-stone-400" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="mt-5 pt-4 border-t border-stone-100 flex flex-wrap gap-2">
        <button
          data-testid="scrapfly-retry-blocked-btn"
          onClick={() => retry(false)}
          disabled={retrying}
          className="text-xs px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-50 flex items-center gap-1"
        >
          {retrying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Retry blocked UK unis
        </button>
        <button
          data-testid="scrapfly-retry-blocked-empty-btn"
          onClick={() => retry(true)}
          disabled={retrying}
          className="text-xs px-3 py-1.5 rounded border border-stone-300 text-stone-800 hover:bg-stone-50 disabled:opacity-50 flex items-center gap-1"
        >
          {retrying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Also retry high-value "empty" hits
        </button>
      </div>

      <p className="mt-3 text-[11px] text-stone-500 leading-relaxed">
        Retrying a batch flips their <code>offer_rate_extraction_status</code> back
        to <code>retry</code>; the extraction daemon then re-runs them through the
        Scrapfly proxy ladder on its next 20-min tick.
      </p>
    </div>
  );
}
